<template>
  <v-card class="ModalInviteMembers" max-width="600px">
    <h4 class="ModalInviteMembers__title">Invite Members</h4>
    <div v-if="!saving">
      <span class="ModalInviteMembers__label">{{ inviteDescription }}</span>
      <div class="ModalInviteMembers__form">
        <div class="ModalInviteMembers__members_block">
          <div class="members_list">
            <div class="chip" v-for="(member, i) of members" :key="member">
              {{ member }}
              <v-icon class="chip_close_icon" @click="removeMember(i)">
                mdi-window-close
              </v-icon>
            </div>
          </div>
          <input
            class="members_input"
            :class="wrongInput ? 'error_input' : ''"
            @keypress.space="addMember"
            v-model="currentInput"
            @keypress.enter.prevent="addMember"
            placeholder="Add email and press Enter"
          />
        </div>

        <div v-if="showOrgMemberPicker" class="ModalInviteMembers__org_members">
          <div class="ModalInviteMembers__org_members_header">
            <span class="ModalInviteMembers__org_members_label">
              Organisation members
            </span>
            <v-text-field
              v-if="organisationMembers.length > 5"
              v-model="orgMemberFilter"
              prepend-inner-icon="mdi-magnify"
              placeholder="Search members"
              class="ModalInviteMembers__org_members_search"
              hide-details
              dense
              outlined
              dark
            />
          </div>
          <div v-if="loadingOrgMembers" class="ModalInviteMembers__org_members_status">
            Loading organisation members...
          </div>
          <div
            v-else-if="!filteredOrgMembers.length"
            class="ModalInviteMembers__org_members_status"
          >
            {{ orgMembersEmptyMessage }}
          </div>
          <div v-else class="ModalInviteMembers__org_members_list">
            <button
              v-for="member in filteredOrgMembers"
              :key="member.user_id || member.email"
              type="button"
              class="ModalInviteMembers__org_member"
              @click="addOrgMember(member)"
            >
              <span class="ModalInviteMembers__org_member_text">
                <span class="ModalInviteMembers__org_member_name">
                  {{ memberDisplayName(member) }}
                </span>
                <span class="ModalInviteMembers__org_member_email">
                  {{ memberSubtitle(member) }}
                </span>
              </span>
              <v-icon small class="ModalInviteMembers__org_member_icon">
                mdi-plus
              </v-icon>
            </button>
          </div>
        </div>

        <div class="ModalInviteMembers__roles_block" v-if="showRolePicker">
          <v-select
            :value="membersRoleInput"
            @change="changeRoleInput"
            :items="normalizedRoles"
            item-text="label"
            item-value="value"
            :menu-props="{
              contentClass: 'ModalInviteMembers__roles_menu',
              bottom: true,
              offsetY: true,
            }"
            class="ModalInviteMembers__select_roles"
            color="#5e636d"
            return-object
            :disabled="rolesDisabled"
            outlined
            hide-details
            dense
          >
          </v-select>
        </div>
      </div>
      <div v-if="errorMain" class="error-main mt-4 pa-4">
        {{ errorMain }}
      </div>
      <div class="ModalInviteMembers__footer">
        <v-btn @click="close" color="n5" class="">Cancel</v-btn>
        <v-btn @click="sendInvitation" color="y1">Send Invitation</v-btn>
      </div>
    </div>
    <div v-else class="spinner">
      <FlamePreloader :compact="true" :size="100" />
    </div>
  </v-card>
</template>

<script>
import FlamePreloader from '@/components/_Common/FlamePreloader'
import {
  getOrgRoleLabel,
  isInvitableOrgProjectMember,
} from '@/utils/membershipPermissions'

export default {
  name: 'ModalInviteMembers',
  components: { FlamePreloader },
  props: {
    inviteAction: {
      type: Number,
      default: 1,
    },
    roles: {
      type: Array,
      default: () => [],
    },
    rolesDisabled: {
      type: Boolean,
      default: true,
    },
    defaultRole: {
      type: String,
      default: null,
    },
    inviteDescription: {
      type: String,
      default: "Enter your coworkers' emails and select their roles",
    },
  },
  data() {
    return {
      errorMain: '',
      saving: false,
      members: [],
      currentInput: '',
      selectedRole: null,
      wrongInput: false,
      membersRoleInput: null,
      loadingOrgMembers: false,
      orgMemberFilter: '',
    }
  },
  computed: {
    membersAdded() {
      return this.members.length > 0
    },
    normalizedRoles() {
      return this.roles.map((role) => {
        if (typeof role === 'string') {
          return { label: role, value: role.toLowerCase() }
        }
        return role
      })
    },
    showRolePicker() {
      return this.normalizedRoles.length > 0
    },
    showOrgMemberPicker() {
      return (
        this.inviteAction === 2 &&
        this.$store.state.organisations.current.id !== 0
      )
    },
    organisationMembers() {
      return this.$store.state.organisation_user.list || []
    },
    excludedEmails() {
      const emails = new Set(
        this.members.map((email) => email.toLowerCase().trim())
      )

      this.$store.state.project_user.list.forEach((member) => {
        if (member.email) emails.add(member.email.toLowerCase().trim())
      })

      this.$store.state.invitation.list
        .filter((invitation) => invitation.type === 'Project')
        .forEach((invitation) => {
          if (invitation.email) emails.add(invitation.email.toLowerCase().trim())
        })

      return emails
    },
    selectableOrgMembers() {
      const org = this.$store.state.organisations.current
      return this.organisationMembers.filter((member) => {
        if (!isInvitableOrgProjectMember(member, org)) return false
        return !this.excludedEmails.has(member.email.toLowerCase().trim())
      })
    },
    filteredOrgMembers() {
      const query = this.orgMemberFilter.trim().toLowerCase()
      if (!query) return this.selectableOrgMembers

      return this.selectableOrgMembers.filter((member) => {
        const name = (member.name || '').toLowerCase()
        const email = member.email.toLowerCase()
        return name.includes(query) || email.includes(query)
      })
    },
    orgMembersEmptyMessage() {
      if (this.organisationMembers.length === 0) {
        return 'No organisation members found.'
      }
      if (this.selectableOrgMembers.length === 0) {
        return this.projectTerms.implicitAccessInviteHint
      }
      return 'No members match your search.'
    },
    projectTerms() {
      return this.$store.getters['organisations/projectTerms']
    },
  },
  created() {
    this.setInitialRole()
    if (this.showOrgMemberPicker) this.loadOrganisationMembers()
  },
  methods: {
    async loadOrganisationMembers() {
      this.loadingOrgMembers = true
      try {
        await this.$store.dispatch('organisation_user/load')
      } catch (e) {
        console.log(e)
      } finally {
        this.loadingOrgMembers = false
      }
    },
    memberDisplayName(member) {
      if (member.name) return member.name
      return member.email
    },
    memberSubtitle(member) {
      const parts = []
      if (member.name) parts.push(member.email)
      if (member.role) parts.push(getOrgRoleLabel(member.role))
      return parts.join(' · ')
    },
    addOrgMember(member) {
      const email = member.email.trim()
      if (!email || this.excludedEmails.has(email.toLowerCase())) return
      this.members.push(email)
      this.errorMain = ''
      this.wrongInput = false
    },
    setInitialRole() {
      if (!this.normalizedRoles.length) {
        this.membersRoleInput = null
        this.selectedRole = null
        return
      }
      const defaultValue = this.defaultRole || this.normalizedRoles[0].value
      const match =
        this.normalizedRoles.find((role) => role.value === defaultValue) ||
        this.normalizedRoles[0]
      this.membersRoleInput = match
      this.selectedRole = match.value
    },
    sendInvitation() {
      if (this.currentInput.length > 0 && this.members.length == 0)
        this.addMember()
      if (this.members.length == 0) {
        this.errorMain = 'You should add email to continue'
        this.wrongInput = true
        return
      }
      if (this.inviteAction == 1) this.sendInvitationForSite()
      else if (this.inviteAction == 2) this.sendInvitationForProject()
      else if (this.inviteAction == 3) this.sendInvitationForOrgs()
    },
    sendInvitationForSite() {
      this.saving = true
      this.$store
        .dispatch('site_user/addUsers', {
          site_id: this.activeSite.id,
          emails: this.members,
        })
        .then(() => {
          this.saving = false
          this.$emit('close')
        })
        .catch((e) => {
          this.saving = false
          console.log(e)
        })
    },
    sendInvitationForProject() {
      this.saving = true
      this.errorMain = ''
      const params = {
        project_id: this.$store.state.projects.active.id,
        emails: this.members,
      }
      if (this.selectedRole) params.role = this.selectedRole
      this.$store
        .dispatch('project_user/addUsers', params)
        .then(() => {
          this.saving = false
          this.$emit('close')
        })
        .catch((e) => {
          this.saving = false
          this.errorMain = this.inviteErrorMessage(e)
          console.log(e)
        })
    },
    sendInvitationForOrgs() {
      this.saving = true
      this.$store
        .dispatch('organisation_user/addUsers', {
          organisation_id: this.$store.state.organisations.current.id,
          emails: this.members,
          role: this.selectedRole,
        })
        .then(() => {
          this.saving = false
          this.$emit('close')
        })
        .catch((e) => {
          this.saving = false
          console.log(e)
        })
    },
    close() {
      this.$emit('close')
    },
    addMember() {
      if (this.currentInput.length == 0) return
      this.validateEmail(this.currentInput.trim())
      const memberAlreadyAdded = this.members.includes(this.currentInput.trim())
      if (!this.wrongInput) {
        if (!memberAlreadyAdded) this.members.push(this.currentInput.trim())
        this.currentInput = ''
        this.wrongInput = false
        this.errorMain = ''
      }
    },
    removeMember(index) {
      this.members.splice(index, 1)
    },
    changeRoleInput(role) {
      this.membersRoleInput = role
      this.selectedRole = role.value
    },
    validateEmail(email) {
      const reg =
        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      this.wrongInput = !email.toLowerCase().match(reg)
    },
    inviteErrorMessage(error) {
      if (error?.body?.message) return error.body.message
      if (error?.message) return error.message
      return this.projectTerms.addMemberFailed
    },
  },
}
</script>

<style lang="scss">
$style: ModalInviteMembers;
.#{$style} {
  &.theme--dark.v-card {
    @extend %modal-body-styles;
    min-width: 560px;
    max-width: 560px !important;
    padding: 24px 24px 0;
  }
  &__label {
    @extend %label-styles;
    display: block;
    margin-bottom: 16px;
  }
  &__form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 8px;
  }
  &__members_block {
    width: 100%;
    min-height: 40px;
    border: 1px solid $N6;
    border-radius: 8px;
    color: $N1;

    .members_list {
      margin-left: 8px;
      .chip {
        display: inline-block;
        padding: 3px 8px;
        border-radius: 8px;
        border: 1px solid $N7;
        margin-left: 10px;
        margin-top: 10px;

        .chip_close_icon {
          font-size: 16px;
          vertical-align: middle;
          &:focus {
            outline-width: 0;
          }
        }
      }
    }
    .members_input {
      height: 40px;
      width: 100%;
      outline-width: 0;
      padding: 10px 12px;
      color: inherit;
      background: transparent;
      border: none;
      box-sizing: border-box;

      &::placeholder {
        color: $N3;
      }

      &.error_input {
        color: red;
      }
    }
  }
  &__roles_block {
    width: 100%;
    max-width: 220px;
  }
  &__select_roles {
    border-radius: 8px;

    fieldset {
      border-color: $N6 !important;
    }
  }
  &__org_members {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  &__org_members_header {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  &__org_members_label {
    color: $N2;
    font-size: 13px;
    font-weight: 500;
  }
  &__org_members_search {
    max-width: 100%;

    .v-input__slot {
      min-height: 36px !important;
    }
  }
  &__org_members_status {
    color: $N3;
    font-size: 13px;
    padding: 8px 0;
  }
  &__org_members_list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 220px;
    overflow-y: auto;
    padding-right: 4px;
  }
  &__org_member {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 10px 12px;
    border: 1px solid $N6;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.02);
    color: $N1;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.15s ease, background 0.15s ease;

    &:hover {
      border-color: $N4;
      background: rgba(255, 255, 255, 0.05);
    }
  }
  &__org_member_text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  &__org_member_name {
    color: $white;
    font-size: 14px;
    font-weight: 500;
  }
  &__org_member_email {
    color: $N3;
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__org_member_icon {
    color: $Y1 !important;
    flex-shrink: 0;
    margin-left: 12px;
  }
  &__title {
    font-weight: 500;
    font-size: $H16;
    color: $white;
    margin-bottom: 32px;
  }
  &__footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-right: 15px;
    padding: 16px 0;
    gap: 16px;
    & > .theme--dark.v-btn {
      color: $white;
    }
  }
  .error-main {
    background: #e94a53;
    border-radius: 2px;
  }

  .spinner {
    display: flex;
    width: 100%;
    min-width: 150px;
    height: 100%;
    align-items: center;
    margin-bottom: 15px;
    justify-content: center;
  }
  &__roles_menu {
    .v-list {
      background: $N4 !important;
      min-width: 118px !important;
      border-radius: 8px;
    }
  }
}
</style>
